// YouTube IFrame playback source. The cursor is slaved to player.getCurrentTime() (the IFrame is
// its own clock), matching the production pattern. Implements PlaybackSource so the cursor loop
// treats it like the file player.
import type { PlaybackSource } from "./PlaybackSource";

declare global {
  interface Window { YT?: any; onYouTubeIframeAPIReady?: () => void }
}

let apiPromise: Promise<void> | null = null;
function loadYouTubeApi(): Promise<void> {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise<void>((resolve) => {
    if (window.YT && window.YT.Player) { resolve(); return; }
    window.onYouTubeIframeAPIReady = () => resolve();
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(s);
  });
  return apiPromise;
}

export class YouTubeController implements PlaybackSource {
  private player: any = null;
  videoId: string | null = null;

  async load(videoId: string, hostId = "yt-host"): Promise<void> {
    this.videoId = videoId;
    await loadYouTubeApi();
    // cueVideoById (not loadVideoById+pause): shows the thumbnail + play button in a "cued" state.
    // loadVideoById tries to autoplay — blocked on mobile — and pausing it leaves a dead black frame
    // that ignores taps, while only the app's playVideo() worked.
    if (this.player && this.player.cueVideoById) {
      try { this.player.cueVideoById(videoId); return; } catch { /* recreate */ }
    }
    const host = document.getElementById(hostId);
    if (!host) return;
    this.player = new window.YT.Player(hostId, {
      videoId,
      playerVars: { playsinline: 1, controls: 1, rel: 0, modestbranding: 1 },
    });
  }

  getPlaybackTime(): number {
    return this.player && this.player.getCurrentTime ? this.player.getCurrentTime() : 0;
  }
  seek(t: number): void { if (this.player?.seekTo) this.player.seekTo(Math.max(0, t), true); }
  // move the playhead without playing: seekTo auto-plays a cued/paused video, so re-pause immediately
  seekSilent(t: number): void {
    if (!this.player?.seekTo) return;
    this.player.seekTo(Math.max(0, t), true);
    try { this.player.pauseVideo?.(); } catch { /* noop */ }
  }
  play(): void { this.player?.playVideo?.(); }
  pause(): void { this.player?.pauseVideo?.(); }
  stop(): void { try { this.player?.pauseVideo?.(); } catch { /* noop */ } }
  isPlaying(): boolean {
    return !!(this.player?.getPlayerState && this.player.getPlayerState() === 1);
  }
  get duration(): number { return this.player?.getDuration ? this.player.getDuration() : 0; }
  getTitle(): string { try { return this.player?.getVideoData?.()?.title || ""; } catch { return ""; } }
  setVolume(v: number): void { try { this.player?.setVolume?.(Math.round(v)); } catch { /* noop */ } }
}
